/**
 * Email Helper Utilities
 * 
 * Common functions for email formatting, validation, and parsing
 */

import type { EmailParticipant, ThreadParticipant } from '../types/entities';

// ==================== Formatting ====================

/**
 * Format timestamp relative to now
 */
export function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  
  // Less than 1 minute
  if (diff < 60 * 1000) {
    return 'Just now';
  }
  
  // Less than 1 hour
  if (diff < 60 * 60 * 1000) {
    const mins = Math.floor(diff / (60 * 1000));
    return `${mins}m ago`;
  }
  
  // Less than 24 hours: show time
  if (diff < 24 * 60 * 60 * 1000) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }
  
  // Less than 7 days: show day
  if (diff < 7 * 24 * 60 * 60 * 1000) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  }
  
  // Less than 1 year: show month and day
  if (diff < 365 * 24 * 60 * 60 * 1000) {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }
  
  // Older: show year too
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format participant list for display
 */
export function formatParticipants(
  participants: EmailParticipant[] | ThreadParticipant[],
  maxCount: number = 3
): string {
  const names = participants
    .slice(0, maxCount)
    .map(p => p.name || p.email.split('@')[0]);
  
  if (participants.length > maxCount) {
    return `${names.join(', ')} +${participants.length - maxCount}`;
  }
  
  return names.join(', ');
}

/**
 * Get display name from participant
 */
export function getParticipantDisplay(participant: EmailParticipant): string {
  return participant.name || participant.email;
}

/**
 * Format file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

// ==================== Validation ====================

/**
 * Validate email address format
 */
export function isValidEmail(email: string): boolean {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

/**
 * Parse comma-separated email list
 */
export function parseEmailList(input: string): EmailParticipant[] {
  return input
    .split(',')
    .map(s => s.trim())
    .filter(s => s.length > 0)
    .map(s => {
      // Handle "Name <email@example.com>" format
      const match = s.match(/^(.+?)\s*<(.+?)>$/);
      if (match) {
        return { name: match[1].trim(), email: match[2].trim() };
      }
      // Handle plain email
      return { email: s };
    })
    .filter(p => isValidEmail(p.email));
}

// ==================== Participant Merging ====================

/**
 * Merge participants from multiple messages, removing duplicates
 */
export function mergeParticipants(
  existing: ThreadParticipant[],
  incoming: ThreadParticipant[]
): ThreadParticipant[] {
  const merged = [...existing];
  
  for (const p of incoming) {
    if (!merged.some(e => e.email === p.email && e.role === p.role)) {
      merged.push(p);
    }
  }
  
  return merged;
}

/**
 * Extract participants from message for thread building
 */
export function extractThreadParticipants(message: {
  from: EmailParticipant[];
  to: EmailParticipant[];
  cc?: EmailParticipant[];
}): ThreadParticipant[] {
  const participants: ThreadParticipant[] = [];
  
  message.from.forEach(p => {
    participants.push({ ...p, role: 'from' as const });
  });
  
  message.to.forEach(p => {
    participants.push({ ...p, role: 'to' as const });
  });
  
  (message.cc || []).forEach(p => {
    participants.push({ ...p, role: 'cc' as const });
  });
  
  return participants;
}

// ==================== Label Utilities ====================

/**
 * Union of label arrays (remove duplicates)
 */
export function unionLabels(existing: string[], incoming: string[]): string[] {
  return Array.from(new Set([...existing, ...incoming]));
}

/**
 * Check if label set includes any of the given labels
 */
export function hasAnyLabel(labels: string[], check: string[]): boolean {
  return check.some(c => labels.includes(c));
}

/**
 * Get smart label display name
 */
export function getSmartLabelDisplay(label: string): string {
  const map: Record<string, string> = {
    personal: 'Personal',
    work: 'Work',
    team: 'Team',
    receipts: 'Receipts',
    travel: 'Travel',
    finance: 'Finance',
    notifications: 'Notifications',
    newsletter: 'Newsletter',
    support: 'Support',
    social: 'Social',
    promotions: 'Promotions',
    system: 'System',
    unknown: 'Unknown',
  };
  return map[label] || label;
}

// ==================== HTML Sanitization ====================

/**
 * Basic HTML sanitization for email display
 * TODO: Use a proper sanitizer library (DOMPurify)
 */
export function sanitizeHtml(html: string): string {
  // Very basic sanitization - replace with DOMPurify in production
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/on\w+\s*=\s*[^\s>]*/gi, '');
}

/**
 * Extract plain text from HTML
 */
export function htmlToPlainText(html: string): string {
  // Remove HTML tags
  const text = html.replace(/<[^>]*>/g, ' ');
  // Collapse whitespace
  return text.replace(/\s+/g, ' ').trim();
}

// ==================== Thread ID Generation ====================

/**
 * Generate River thread ID from integration and provider thread ID
 */
export function generateThreadId(integrationId: string, providerThreadId: string): string {
  return `${integrationId}:${providerThreadId}`;
}

/**
 * Parse River thread ID into components
 */
export function parseThreadId(threadId: string): { integrationId: string; providerThreadId: string } | null {
  const parts = threadId.split(':');
  if (parts.length !== 2) return null;
  return {
    integrationId: parts[0],
    providerThreadId: parts[1],
  };
}
